import {DraggableVisualisation} from "./VisualisationComponent.js";
import OperationNotAllowedError from "../../entities/error/OperationNotAllowedError.js";
import Messages from "../../util/Messages.js";
import VisualizationSelectors from "./VisualizationSelectors.js";
import {COLUMN_TYPE} from "./TableColumn.js";

export default class VisualizationGrid {
    /**
     * @param {BatchMeta} batchMeta
     * @param {HTMLElement} rootElement
     * @param {BatchRepository} batchRepository
     */
    constructor(batchMeta, rootElement, batchRepository) {
        this.batchMeta = batchMeta;
        this.grid = rootElement;
        /** @type {VisualisationComponent[]} */
        this.components = [];
        this.batchRepository = batchRepository;
        this.#addListeners();
    }

    /**
     * @param {{location:{row:number, col:number}}[]} placeholders
     */
    createPlaceholders(placeholders) {
        for (const placeholder of placeholders) {
            const rows = [...this.grid.querySelectorAll('.vis-grid-row')];
            const missingRows = rows.length - 1 - placeholder.location.row;
            for (let i = missingRows; i < 0; i++)
                this.appendRow();
            let row = this.grid.querySelector(`.vis-grid-row:nth-child(${placeholder.location.row + 1})`);
            const cols = [...row.querySelectorAll('.vis-placeholder')];
            const missingColumns = cols.length - 1 - placeholder.location.col;
            for (let i = missingColumns; i < 0; i++)
                this.appendPlaceholder(row);
        }
    }

    /** @param {VisualisationComponent} vis */
    addVisualization(vis) {
        this.components.push(vis);
        let location = vis._props.card.location;
        if (location !== null){ // add existing visualization
            const str = location.split(":"), row = parseInt(str[0]) + 1, col = parseInt(str[1]) + 1;
            let placeholder = this.grid.querySelector(`.vis-grid-row:nth-child(${row}) .vis-placeholder:nth-child(${col})`);
            if (placeholder){
                placeholder.parentNode.replaceChild(vis._root, placeholder)
            }
        } else if (vis._props.card.parentLocation){ // add duplicated visualization
            vis._props.card.location = vis._props.card.parentLocation;
            const rowIndex = parseInt(vis._props.card.parentLocation.split(":")[0]);
            delete vis._props.card.parentLocation;
            const rows = this.grid.querySelectorAll('.vis-grid-row');
            const parentRow = rows[rowIndex];
            if (parentRow.querySelectorAll('.visualisation-component').length >= DraggableVisualisation.maxCardNumber) {
                const newRow = document.createElement('div');
                newRow.classList.add('vis-grid-row');
                parentRow.insertAdjacentElement('afterend', newRow);
                newRow.insertAdjacentElement('beforeend', vis._root)
            } else {
                parentRow.insertAdjacentElement('beforeend', vis._root)
            }
            this._updateAllLocations();
            vis.markVisualizationAsNew();
            window.scrollTo({
                // Because header has fixed position, and we don't want to overlap the vis cards with it
                // 70 - a little more than the header height (4rem)
                top: vis._root.getBoundingClientRect().top + window.scrollY - 70,
                behavior: 'smooth'
            });
        }  else { // add new visualization
            const row = document.createElement('div');
            row.classList.add('vis-grid-row');
            row.insertAdjacentElement("afterbegin", vis._root);
            vis.markVisualizationAsNew();
            this.grid.insertAdjacentElement('afterbegin', row);
            this._updateAllLocations();
            window.scrollTo({
                // Because header has fixed position, and we don't want to overlap the vis cards with it
                // 70 - a little more than the header height (4rem)
                top: vis._root.getBoundingClientRect().top + window.scrollY - 70,
                behavior: 'smooth'
            });
        }
        vis._grid = this;
        new DraggableVisualisation(vis._root, this);
    }

    appendRow() {
        let row = document.createElement('div');
        row.classList.add('vis-grid-row');
        return this.grid.appendChild(row)
    }

    /**
     * @param {HTMLElement} row
     * @returns {*}
     */
    appendPlaceholder(row) {
        let stub = document.createElement('div');
        stub.classList.add('visualisation-component', 'vis-placeholder');
        stub.innerHTML = '<div class="dancing-bars"></div>'
        return row.appendChild(stub)
    }

    /**
     * @param {VisualisationComponent} vis
     * @returns {Promise<void>}
     */
    async onVisRemoved(vis){
        this.grid.querySelectorAll('.vis-grid-row').forEach(row => {
            if (row.childNodes.length === 0)
                row.remove()
        })
        this.components = this.components.filter(c=>c!==vis);
        this._updateAllLocations();
        await this.saveProperties();
    }

    async saveProperties(){
        if (!this.batchMeta.isWritable())
            throw new OperationNotAllowedError(1, Messages.BATCH.CHANGES_WILL_NOT_BE_SAVED_IN_READ_ONLY_MODE);
        await this.#saveVisualizations();
    }

    /** @param {VisualisationComponent} currentVisComponent */
    removeCellSelectionFromNotCurrentVisualisation(currentVisComponent){
        for (const vis of this.components){
            if (vis !== currentVisComponent){
                vis.unselectActiveCell()
            }
        }
    }

    async #saveVisualizations(){
        const data = this.components.map(comp => comp._props);
        await this.batchRepository.saveVisualizations(this.batchMeta.id, data);
    }

    getLastRowIndex() {
        return Array.from(this.grid.querySelectorAll('.vis-grid-row')).length - 1;
    }
    _locationOf(visualization){
        let rows = Array.from(this.grid.querySelectorAll('.vis-grid-row'));
        const row = visualization._root.closest('.vis-grid-row');
        const cols = Array.from(row.querySelectorAll('.visualisation-component'));
        return rows.indexOf(row) + ':' + cols.indexOf(visualization._root)
    }

    _updateAllLocations(){
        for (const component of this.components) {
            component._props.card.location = this._locationOf(component)
        }
    }

    #addListeners(){
        document.addEventListener('keydown', (evt)=>{
            if ((evt.ctrlKey || evt.metaKey) && evt.key === "c") {
                const selectedCell = document.querySelector(`${VisualizationSelectors.CLASS_NAMES.activeCellInSelectedRow}`);
                if (selectedCell){
                    if (selectedCell.getAttribute('data-type') === COLUMN_TYPE.SVG) {
                        navigator.clipboard.writeText(selectedCell.innerHTML);
                    } else {
                        // we use additional selector because in row can be warning icon, and we want to get only cell data
                        navigator.clipboard.writeText(selectedCell.querySelector('.v-table-body-row-cell__data').textContent)
                    }
                }
            }
        })
        document.addEventListener('click', (evt)=>{
            if (!this.grid.contains(evt.target)){
                const selectedEl = document.querySelector(`${VisualizationSelectors.CLASS_NAMES.activeCellInSelectedRow}`);
                if (selectedEl)
                    selectedEl.classList.remove(`${VisualizationSelectors.CLASS_NAMES.selectedCellClassName}`)
            }
        })
    }
}